import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ExecutionGrantDurationField } from '../../components/ExecutionGrantDurationField';
import { parseExecutionGrantDuration, type ExecutionGrantDurationUnit } from '../../utils/executionGrantDuration';
import { browserGet, browserMutate } from './client';
import type { ApiClient } from '../../api/client';

type Target = {
  delegation_id: string; conversation_id: string | null; target_token_id: string | null;
  operations: string[]; modes: string[]; executor_refs: string[]; max_ttl_minutes: number; expires_at: string;
};
type Grant = { id: string; name: string; modes: string[]; executor_refs: string[]; expires_at: string; revoked_at: string | null };
type Created = { grant: Grant; key: string };
export type GrantTargetFilter = { kind: 'token' } | { kind: 'conversation'; id: string };

export function filterGrantTargets(targets: Target[], filter?: GrantTargetFilter): Target[] {
  if (filter?.kind === 'token') return targets.filter(target => typeof target.target_token_id === 'string' && target.conversation_id === null);
  if (filter?.kind === 'conversation') return targets.filter(target => target.conversation_id === filter.id && target.target_token_id === null);
  return targets;
}

function GrantForm({ target, csrf = '', apiClient, onDenied }: {
  target: Target; csrf?: string; apiClient?: ApiClient; onDenied: () => Promise<void>;
}) {
  const id = useId();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('1');
  const [unit, setUnit] = useState<ExecutionGrantDurationUnit>('minutes');
  const [modes, setModes] = useState<string[]>([]);
  const [executors, setExecutors] = useState<string[]>([]);
  const [rows, setRows] = useState<Grant[]>([]);
  const [created, setCreated] = useState<Created | null>(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now);
  const generation = useRef(0);
  const prefix = apiClient ? '/api' : '/api/browser';
  const base = target.target_token_id
    ? `${prefix}/tokens/${encodeURIComponent(target.target_token_id)}/exec-grants`
    : `${prefix}/conversations/${encodeURIComponent(target.conversation_id!)}/exec-grants`;
  const query = `?delegation_id=${encodeURIComponent(target.delegation_id)}`;
  const maximum = Math.max(0, Math.min(target.max_ttl_minutes, Math.floor((Date.parse(target.expires_at) - now) / 60_000)));
  const duration = parseExecutionGrantDuration(amount, unit, maximum);

  const load = useCallback(async () => {
    const request = ++generation.current;
    setCreated(null); setRows([]);
    try {
      const grants = await (apiClient?.get ?? browserGet)<Grant[]>(base + query);
      if (request === generation.current) setRows(grants);
    } catch {
      if (request === generation.current) { setStatus('Grant authority unavailable.'); await onDenied(); }
    }
  }, [apiClient, base, onDenied, query]);

  useEffect(() => {
    let canceled = false;
    queueMicrotask(() => { if (!canceled) void load(); });
    return () => { canceled = true; generation.current += 1; };
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const timestamp = Date.now();
      setNow(timestamp);
      setCreated((value) => value && Date.parse(value.grant.expires_at) <= timestamp ? null : value);
      if (Date.parse(target.expires_at) <= timestamp) {
        generation.current += 1;
        setCreated(null); setRows([]);
        window.clearInterval(timer);
        void onDenied();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [onDenied, target.expires_at]);

  useEffect(() => {
    if (!created) return;
    const expiry = Math.min(Date.parse(created.grant.expires_at), Date.parse(target.expires_at));
    const timer = window.setTimeout(() => setCreated(null), Math.max(0, expiry - Date.now()));
    return () => window.clearTimeout(timer);
  }, [created, target.expires_at]);

  async function create(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!duration.minutes || !name.trim() || !modes.length || !executors.length || busy) return;
    const request = ++generation.current;
    setBusy(true); setCreated(null);
    try {
      const body = {
        delegation_id: target.delegation_id, name: name.trim(), modes, executor_refs: executors,
        ttl_minutes: duration.minutes, max_uses: 1
      };
      const value = apiClient ? await apiClient.post<Created>(base, body) : await browserMutate<Created>(base, csrf, 'POST', body);
      if (request !== generation.current) return;
      if (!value?.key || !value.grant) throw new Error('Grant creation returned no key.');
      setRows((previous) => [value.grant, ...previous]); setCreated(value); setStatus('Grant created.');
    } catch (error) {
      if (request !== generation.current) return;
      setCreated(null); setRows([]); setStatus(error instanceof Error ? error.message : 'Grant unavailable.');
      await onDenied();
    } finally { if (request === generation.current) setBusy(false); }
  }

  async function revoke(grantId: string) {
    const request = ++generation.current;
    setCreated(null); setBusy(true);
    try {
      const path = `${base}/${encodeURIComponent(grantId)}${query}`;
      if (apiClient) await apiClient.del(path);
      else await browserMutate(path, csrf, 'DELETE');
      if (request !== generation.current) return;
      setRows((previous) => previous.filter((grant) => grant.id !== grantId)); setStatus('Grant revoked.');
    } catch {
      if (request !== generation.current) return;
      setCreated(null); setRows([]); await onDenied();
    } finally { if (request === generation.current) setBusy(false); }
  }

  function toggle(values: string[], value: string): string[] { return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]; }
  return <section className="card" aria-label={target.target_token_id ? 'Token execution grants' : 'Conversation execution grants'}>
    <h2>{target.target_token_id ? 'Token execution grants' : 'Conversation execution grants'}</h2>
    <p>Exact target: {target.target_token_id ?? target.conversation_id}</p>
    <p>Administrator delegation expires {new Date(target.expires_at).toLocaleString()}. Keys expire within this lease and are single-use.</p>
    <button type="button" disabled={busy} onClick={() => void load()}>Refresh grants</button>
    <p role="status">{status}</p>
    {target.operations.includes('create') && maximum >= 1 && <form onSubmit={(event) => void create(event)}>
      <label htmlFor={`${id}-name`}>Grant name</label>
      <input id={`${id}-name`} value={name} maxLength={100} disabled={busy} onChange={(event) => setName(event.target.value)} />
      <fieldset disabled={busy}><legend>Permitted modes</legend>{target.modes.map((mode) => <label key={mode}>
        <input type="checkbox" checked={modes.includes(mode)} onChange={() => setModes(toggle(modes, mode))} />{mode}
      </label>)}</fieldset>
      <fieldset disabled={busy}><legend>Exact executors</legend>{target.executor_refs.map((executor) => <label key={executor}>
        <input type="checkbox" checked={executors.includes(executor)} onChange={() => setExecutors(toggle(executors, executor))} />{executor}
      </label>)}</fieldset>
      <ExecutionGrantDurationField amount={amount} unit={unit} onAmountChange={setAmount} onUnitChange={setUnit} maxMinutes={maximum} disabled={busy} />
      <button type="submit" disabled={busy || !!duration.error || !name.trim() || !modes.length || !executors.length}>Create execution grant</button>
    </form>}
    {created && <div><p>One-time key — copy now. It is not stored by this page.</p><output aria-label="One-time execution grant key" style={{ overflowWrap: 'anywhere' }}>{created.key}</output></div>}
    <ul>{rows.map((grant) => <li key={grant.id}>{grant.name} — {grant.modes.join(', ')} — expires {new Date(grant.expires_at).toLocaleString()}
      {!grant.revoked_at && target.operations.includes('revoke') && <button type="button" disabled={busy} onClick={() => void revoke(grant.id)}>Revoke {grant.name}</button>}
    </li>)}</ul>
  </section>;
}

export function ToolBrowserGrants({ csrf, apiClient, target, onExpired }: {
  csrf?: string; apiClient?: ApiClient; target?: GrantTargetFilter; onExpired: () => Promise<void>;
}) {
  const [targets, setTargets] = useState<Target[]>([]);
  const [status, setStatus] = useState('');
  const generation = useRef(0);
  const targetKind = target?.kind;
  const conversationId = target?.kind === 'conversation' ? target.id : undefined;
  const invalidate = useCallback(async () => {
    generation.current += 1; setTargets([]); setStatus('Execution-grant delegation expired, revoked, or unavailable.');
    await onExpired();
  }, [onExpired]);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setTargets([]);
    try {
      const inventory = await (apiClient?.get ?? browserGet)<Target[]>(apiClient ? '/api/grant-targets' : '/api/browser/grant-targets');
      const rows = filterGrantTargets(inventory, targetKind === 'conversation' ? { kind: 'conversation', id: conversationId! }
        : targetKind === 'token' ? { kind: 'token' } : undefined);
      if (request === generation.current) { setTargets(rows); setStatus(rows.length ? '' : 'No current execution-grant delegation for this location.'); }
    } catch { if (request === generation.current) await invalidate(); }
  }, [apiClient, conversationId, invalidate, targetKind]);
  useEffect(() => {
    let canceled = false;
    queueMicrotask(() => { if (!canceled) void refresh(); });
    return () => { canceled = true; generation.current += 1; };
  }, [refresh]);
  return <><div className="card"><h2>Delegated execution grants</h2><p role="status">{status}</p>
    <button type="button" onClick={() => void refresh()}>Refresh grant authority</button></div>
    {targets.map((target) => <GrantForm key={target.delegation_id} target={target} csrf={csrf} apiClient={apiClient} onDenied={invalidate} />)}</>;
}
